"use client";

import { useState, useRef, useCallback } from "react";
import type { TaskResult, TaskResultArtifact } from "@/lib/web-client";
import { artifactUrl } from "@/lib/web-client";

interface ResultViewProps {
  result: TaskResult;
  /** When true, a "Wrong format? Re-render as…" control is hidden (share page) */
  readOnly?: boolean;
  /**
   * User-selected download format key (md | txt | docx | csv | html | tsx).
   * Drives the download button's extension/MIME for text/markdown results so
   * the file matches what the user picked. Falls back to the render format
   * when omitted (share/dashboard views have no user selection).
   */
  downloadFormat?: string;
}

import { MarkdownBlock } from "@/components/web/markdown";

// Extract a full HTML document embedded in mixed markdown content (agents
// often return design notes + a ```html fenced block, or raw notes followed
// by a <!DOCTYPE html> document). Returns the prose before the document and
// the document itself, or null when no full document is present.
function extractHtmlDocument(content: string): { before: string; html: string } | null {
  // 1. Prefer a fenced ```html block containing a full document.
  const fence = content.match(/```html\s*\n([\s\S]*?)```/i);
  if (fence && /<!doctype html|<html[\s>]/i.test(fence[1])) {
    return {
      before: content.slice(0, fence.index ?? 0),
      html: fence[1].trim(),
    };
  }
  // 2. Otherwise a raw <!DOCTYPE html> … </html> (or <html> … </html>) block.
  const docMatch = content.match(/(<!doctype html[\s\S]*?<\/html>|<html[\s>][\s\S]*?<\/html>)/i);
  if (docMatch) {
    return {
      before: content.slice(0, docMatch.index ?? 0),
      html: docMatch[1].trim(),
    };
  }
  return null;
}

// ── Download packaging ─────────────────────────────────────────────
// Builds the href/filename/label for downloading text content in the
// user-selected format. .doc is packaged as Word-compatible HTML
// (application/msword) which Word/Pages open natively.
function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

function buildTextDownload(
  downloadKey: string | undefined,
  renderFormat: string | null | undefined,
  content: string,
  taskId: string,
): { href: string; download: string; label: string } {
  // Prefer the user's explicit choice; else infer from the render format.
  const key = (downloadKey ?? (renderFormat === "text" ? "txt" : "md")).toLowerCase();
  switch (key) {
    case "txt":
      return {
        href: `data:text/plain;charset=utf-8,${encodeURIComponent(content)}`,
        download: `botwork-${taskId}.txt`,
        label: "Download .txt",
      };
    case "doc":
    case "docx": {
      const body = content
        .split("\n")
        .map((l) => `<p>${escapeHtml(l) || "&nbsp;"}</p>`)
        .join("");
      const doc =
        "<html xmlns:o='urn:schemas-microsoft-com:office:office' " +
        "xmlns:w='urn:schemas-microsoft-com:office:word' " +
        "xmlns='http://www.w3.org/TR/REC-html40'><head><meta charset='utf-8'></head><body>" +
        body +
        "</body></html>";
      return {
        href: `data:application/msword;charset=utf-8,${encodeURIComponent(doc)}`,
        download: `botwork-${taskId}.doc`,
        label: "Download .doc",
      };
    }
    case "html":
      return {
        href: `data:text/html;charset=utf-8,${encodeURIComponent(content)}`,
        download: `botwork-${taskId}.html`,
        label: "Download .html",
      };
    case "csv":
      return {
        href: `data:text/csv;charset=utf-8,${encodeURIComponent(content)}`,
        download: `botwork-${taskId}.csv`,
        label: "Download .csv",
      };
    case "md":
    default:
      return {
        href: `data:text/markdown;charset=utf-8,${encodeURIComponent(content)}`,
        download: `botwork-${taskId}.md`,
        label: "Download .md",
      };
  }
}

// ── CSV → HTML table ───────────────────────────────────────────────
function CsvTable({ content }: { content: string }) {
  const rows = content.trim().split("\n").slice(0, 101);
  const header = rows[0]?.split(",").map(c => c.trim().replace(/^"|"$/g, "")) ?? [];
  const body = rows.slice(1).map(r => r.split(",").map(c => c.trim().replace(/^"|"$/g, "")));

  return (
    <div style={{ overflowX: "auto", maxHeight: 480, overflowY: "auto" }}>
      <table style={{
        borderCollapse: "collapse",
        width: "100%",
        fontSize: 13,
        fontFamily: "var(--font-plus-jakarta), sans-serif",
      }}>
        <thead>
          <tr>
            {header.map((h, i) => (
              <th key={i} style={{
                background: "rgba(255,255,255,0.06)",
                border: "1px solid rgba(255,255,255,0.08)",
                padding: "8px 12px",
                textAlign: "left",
                color: "var(--text-muted)",
                fontWeight: 600,
                position: "sticky",
                top: 0,
              }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {body.map((row, ri) => (
            <tr key={ri} style={{ background: ri % 2 === 0 ? "transparent" : "rgba(255,255,255,0.02)" }}>
              {row.map((cell, ci) => (
                <td key={ci} style={{
                  border: "1px solid rgba(255,255,255,0.06)",
                  padding: "7px 12px",
                  color: "var(--text-1)",
                }}>{cell}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── Shared button style ────────────────────────────────────────────
const btnStyle: React.CSSProperties = {
  background: "rgba(255,255,255,0.06)",
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 7,
  padding: "5px 12px",
  color: "var(--text-muted)",
  fontSize: 12,
  fontWeight: 500,
  cursor: "pointer",
  fontFamily: "var(--font-plus-jakarta), sans-serif",
};

// ── Clipboard helper ───────────────────────────────────────────────
async function copyToClipboard(text: string): Promise<boolean> {
  try {
    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
  } catch {
    // fall through to execCommand fallback
  }
  // execCommand fallback for non-secure contexts
  try {
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.cssText = "position:fixed;top:0;left:0;opacity:0;pointer-events:none";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}

// ── Copy button (with visual feedback + robust clipboard) ──────────
function CopyButton({ content }: { content: string }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(() => {
    copyToClipboard(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [content]);
  return (
    <button onClick={handleCopy} style={btnStyle}>
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

// ── Code block ─────────────────────────────────────────────────────
function CodeBlock({ content, language }: { content: string; language?: string }) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(() => {
    copyToClipboard(content).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  }, [content]);

  return (
    <div style={{ position: "relative" }}>
      <div style={{
        position: "absolute", top: 8, right: 8,
        display: "flex", gap: 6, zIndex: 1,
      }}>
        {language && (
          <span style={{
            background: "rgba(255,255,255,0.06)",
            borderRadius: 4,
            padding: "2px 8px",
            fontSize: 11,
            color: "var(--text-muted)",
            fontFamily: "monospace",
          }}>{language}</span>
        )}
        <button onClick={copy} style={btnStyle}>
          {copied ? "Copied" : "Copy"}
        </button>
      </div>
      <pre style={{
        background: "#1a1a1a",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 10,
        padding: "40px 20px 20px",
        overflowX: "auto",
        margin: 0,
        fontSize: 13,
        lineHeight: 1.6,
        color: "#E5E2E1",
        fontFamily: "ui-monospace, 'SF Mono', Menlo, Consolas, monospace",
        maxHeight: 520,
        overflowY: "auto",
      }}>
        <code>{content}</code>
      </pre>
    </div>
  );
}

// ── HTML iframe preview ────────────────────────────────────────────
function HtmlPreview({ content, taskId }: { content: string; taskId: string }) {
  const [mode, setMode] = useState<"preview" | "code">("preview");
  const iframeRef = useRef<HTMLIFrameElement>(null);

  const downloadHtml = () => {
    const blob = new Blob([content], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `botwork-${taskId}.html`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const openNew = () => {
    const blob = new Blob([content], { type: "text/html" });
    const url = URL.createObjectURL(blob);
    window.open(url, "_blank");
  };

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10, alignItems: "center" }}>
        <div style={{ display: "flex", background: "rgba(255,255,255,0.04)", borderRadius: 8, padding: 3 }}>
          {(["preview", "code"] as const).map(m => (
            <button key={m} onClick={() => setMode(m)} style={{
              ...btnStyle,
              background: mode === m ? "rgba(255,255,255,0.1)" : "transparent",
              border: "none",
              fontWeight: mode === m ? 600 : 400,
            }}>
              {m === "preview" ? "Preview" : "Code"}
            </button>
          ))}
        </div>
        <button onClick={openNew} style={btnStyle}>Open in new tab ↗</button>
        <button onClick={downloadHtml} style={btnStyle}>Download .html</button>
      </div>
      {mode === "preview" ? (
        <iframe
          ref={iframeRef}
          srcDoc={content}
          sandbox="allow-scripts allow-same-origin"
          style={{
            width: "100%",
            height: 480,
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 10,
            background: "#fff",
          }}
          title="Result preview"
        />
      ) : (
        <CodeBlock content={content} language="html" />
      )}
    </div>
  );
}

// ── File card list ─────────────────────────────────────────────────
function ArtifactCards({ artifacts, taskId }: { artifacts: TaskResultArtifact[]; taskId: string }) {
  const downloadAll = () => {
    artifacts.forEach(a => {
      const url = artifactUrl(taskId, a.filename);
      const link = document.createElement("a");
      link.href = url;
      link.download = a.filename;
      link.click();
    });
  };

  return (
    <div>
      {artifacts.length > 1 && (
        <button onClick={downloadAll} style={{ ...btnStyle, marginBottom: 12 }}>
          Download all (.zip)
        </button>
      )}
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {artifacts.map(a => (
          <div key={a.filename} style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            background: "rgba(255,255,255,0.04)",
            border: "1px solid rgba(255,255,255,0.08)",
            borderRadius: 10,
            padding: "12px 16px",
          }}>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ color: "var(--text-1)", fontWeight: 600, fontSize: 14, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                {a.filename}
              </div>
              {a.description && (
                <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 2 }}>{a.description}</div>
              )}
            </div>
            <a
              href={artifactUrl(taskId, a.filename)}
              download={a.filename}
              style={{ ...btnStyle, textDecoration: "none" }}
            >
              Download
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main ResultView ────────────────────────────────────────────────
export default function ResultView({ result, readOnly: _readOnly = false, downloadFormat }: ResultViewProps) {
  const { taskId, preview, artifacts } = result;

  // Determine what to render
  if (preview) {
    const { format, content } = preview;

    // Content-sniff: a full HTML document should render as a live preview
    // even when the backend tagged the format as md/text/code. Agents often
    // wrap the HTML in markdown design-notes + a ```html fenced block, so we
    // look for an HTML document ANYWHERE in the content (not just at the
    // start) and extract it. Any prose before the document renders as
    // markdown notes above the preview.
    const looksLikeHtmlDoc = /^\s*(<!doctype html|<html[\s>])/i.test(content);
    const embeddedHtml = !looksLikeHtmlDoc ? extractHtmlDocument(content) : null;

    if (format === "html" || looksLikeHtmlDoc) {
      return (
        <div>
          <HtmlPreview content={content} taskId={taskId} />
        </div>
      );
    }

    if (embeddedHtml) {
      return (
        <div>
          {embeddedHtml.before.trim() && (
            <div style={{ marginBottom: 12 }}>
              <MarkdownBlock content={embeddedHtml.before.trim()} />
            </div>
          )}
          <HtmlPreview content={embeddedHtml.html} taskId={taskId} />
        </div>
      );
    }

    if (format === "csv") {
      return (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <a
              href={`data:text/csv;charset=utf-8,${encodeURIComponent(content)}`}
              download={`botwork-${taskId}.csv`}
              style={{ ...btnStyle, textDecoration: "none" }}
            >
              Download .csv
            </a>
          </div>
          <CsvTable content={content} />
        </div>
      );
    }

    if (format === "code") {
      // Try to detect language from artifacts
      const ext = artifacts[0]?.filename.split(".").pop();
      const langMap: Record<string, string> = {
        ts: "typescript", tsx: "tsx", js: "javascript", jsx: "jsx",
        py: "python", rb: "ruby", go: "go", rs: "rust", html: "html",
        css: "css", json: "json", sh: "bash",
      };
      const language = ext ? (langMap[ext] ?? ext) : undefined;

      return (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            {artifacts[0] && (
              <a
                href={artifactUrl(taskId, artifacts[0].filename)}
                download={artifacts[0].filename}
                style={{ ...btnStyle, textDecoration: "none" }}
              >
                Download {artifacts[0].filename}
              </a>
            )}
          </div>
          <CodeBlock content={content} language={language} />
        </div>
      );
    }

    if (format === "md" || format === "text" || (format as string | null | undefined) == null) {
      // Download button matches the user's selected format (md/txt/doc/…);
      // falls back to the render format when no selection was threaded.
      const dl = buildTextDownload(downloadFormat, format, content, taskId);
      return (
        <div>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <CopyButton content={content} />
            <a
              href={dl.href}
              download={dl.download}
              style={{ ...btnStyle, textDecoration: "none" }}
            >
              {dl.label}
            </a>
          </div>
          <div style={{
            background: "rgba(255,255,255,0.02)",
            border: "1px solid rgba(255,255,255,0.06)",
            borderRadius: 10,
            padding: "20px 24px",
          }}>
            <MarkdownBlock content={content} />
          </div>
        </div>
      );
    }

    // json / binary fallback
    return (
      <div>
        <CodeBlock content={typeof content === "string" ? content : JSON.stringify(content, null, 2)} language={format} />
      </div>
    );
  }

  // No preview — show artifact file cards
  if (artifacts.length > 0) {
    return <ArtifactCards artifacts={artifacts} taskId={taskId} />;
  }

  return (
    <div style={{ color: "var(--text-muted)", fontSize: 14, padding: "20px 0" }}>
      No result content available.
    </div>
  );
}
